<template>
  <v-row align="center" no-gutters>
    <v-col cols="12">
      <v-row no-gutters align="center">
        <v-col cols="auto" class="font-weight-medium pr-2">
          {{ $i18n.t('admin.sendMessage.messageConfiguration') }}
        </v-col>
        <v-col cols="auto">
          <base-rounded-button
            icon="mdi-plus"
            size="x-small"
            color="success"
            @click="toggleDialog({ type: 'input' })"
          />
        </v-col>
      </v-row>
      <v-row align="center" no-gutters class="pt-7">
        <v-col cols="12">
          <ribbon-list
            :listItems="computedConfigurationList"
            no-items-translation-path="default.noData"
            style-type="rawHigher"
            :toggleable="false"
          >
            <template #itemTitle-prepend="{item}">
              {{ item.name }}
              <span class="text-caption font-weight-light">({{ item.protocol === 'sms' ? 'SMS' : 'E-mail' }})</span>
            </template>
            <template #itemButtons-replace="{ item }">
              <dots-menu
                :items="dotsItems"
                @edit="toggleDialog({ type: 'input', item })"
                @delete="toggleDialog({ item })"
              />
            </template>
          </ribbon-list>
        </v-col>
      </v-row>
    </v-col>
    <main-input-dialog
      v-if="isMainInputDialogLoaded"
      :is-visible.sync="isMainInputDialogVisible"
      :title-translation="$i18n.t('admin.sendMessage.messageConfigurate')"
      :agree-text="!!selectedItem ? 'dialog.agreeSave' : 'dialog.agreeAdd'"
      dialog-width="40%"
      :loading="isLoading"
      :inputs="inputs"
      @agree="dialogActionHandler(!!selectedItem ? 'edit' : 'add')"
      @modalClosed="toggleDialog({ type: 'input', value: false })"
    >
      <template v-for="input of inputs" #[`${input.name}.input`]="data">
        <v-col :key="input.name">
          <data-input v-model="payload[input.name]" v-bind="data" />
        </v-col>
      </template>
    </main-input-dialog>
    <main-dialog
      v-if="isMainDialogLoaded"
      :is-visible.sync="isMainDialogVisible"
      :title="$i18n.t('dialog.title.deleteMessageConfiguration')"
      :body="
        $i18n.t('dialog.body.deleteMessageConfigurationConfirmation', {
          protocol: selectedItem.protocol === 'sms' ? 'SMS' : 'E-mail',
          name: selectedItem.name,
        })
      "
      agree-text="dialog.agreeDelete"
      :loading="isLoading"
      @agree="dialogActionHandler('delete')"
      @modalClosed="toggleDialog({ value: false })"
    />
  </v-row>
</template>

<script>
import { call, get } from 'vuex-pathify';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

import RibbonList from '@/components/RibbonList';
import DotsMenu from '@/components/DotsMenu';
import DataInput from '@/components/DataInput';

export default {
  name: 'SendMessageConfiguration',
  components: {
    RibbonList,
    DotsMenu,
    DataInput,
    MainInputDialog: () => import('@/components/MainInputDialog'),
    MainDialog: () => import('@/components/MainDialog'),
  },
  mixins: [prepareAttributesFeatureCardMixin],
  data: () => ({
    payload: {
      protocol: 'email',
    },
    isMainDialogLoaded: false,
    isMainDialogVisible: false,
    isMainInputDialogLoaded: false,
    isMainInputDialogVisible: false,
    isLoading: false,
    dotsItems: [{ name: 'edit' }, { name: 'delete' }],
    selectedItem: null,
  }),
  computed: {
    configurationList: get('sendMessage/configurationList'),
    computedConfigurationList() {
      return this.configurationList.map(el => {
        return { ...el, name: this.layers[el.layer].name };
      });
    },
    layers: get('layers/layers'),
    layersArray() {
      return this.$_objectToArray(this.layers)
        .filter(layer => layer.type === 'features_layer')
        .map(layer => {
          const { name: text, id: value } = layer;
          return { text, value };
        });
    },
    selectedLayerMetadata() {
      return this.layers?.[this.payload.layer] || undefined;
    },
    selectedLayerFormSchema() {
      return this.selectedLayerMetadata?.form_schema?.elements || [];
    },
    selectedLayerDataSourceName() {
      return this.selectedLayerMetadata?.data_source_name || '';
    },
    selectedLayerDataSourceMetadata() {
      return this.metadata?.[this.selectedLayerDataSourceName] || undefined;
    },
    selectedLayerDataSourceAttributesSchema() {
      return this.selectedLayerDataSourceMetadata?.attributes_schema?.attributes || [];
    },
    selectedLayerAttributesArray() {
      return this.$_flattenStructure(
        this.getForm(this.selectedLayerFormSchema, this.selectedLayerDataSourceAttributesSchema, {
          returnDictRelationInfo: true,
        })
      ).map(attribute => {
        const { attribute: value, label: text } = attribute;
        return { value, text };
      });
    },
    inputs() {
      const isEmailProtocol = this.payload.protocol === 'email';
      return [
        {
          name: 'protocol',
          translationPath: 'dialog.messageProtocol',
          dataType: { name: 'select' },
          items: [
            { value: 'email', text: 'E-mail' },
            { value: 'sms', text: 'SMS' },
          ],
        },
        {
          name: 'layer',
          translationPath: 'dialog.layer',
          subtitle: 'dialog.sendMessageLayerSubtitle',
          dataType: { name: 'select' },
          items: this.layersArray,
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
        {
          name: 'attribute',
          translationPath: isEmailProtocol ? 'dialog.emailAttribute' : 'dialog.phoneAttribute',
          subtitle: 'default.optional',
          dataType: { name: 'select' },
          items: this.selectedLayerAttributesArray,
          disabled: !this.payload.layer,
        },
        ...(isEmailProtocol
          ? [
              {
                name: 'subject',
                translationPath: 'dialog.emailSubject',
                subtitle: 'dialog.sendMessageOptionalSubtitle',
                dataType: { name: 'text' },
                hideDetails: true,
              },
            ]
          : []),
        {
          name: 'content',
          translationPath: isEmailProtocol ? 'dialog.messageEmailText' : 'dialog.messageSmsText',
          subtitle: 'dialog.sendMessageOptionalSubtitle',
          dataType: { name: 'textarea' },
          hideDetails: true,
          autoGrow: true,
          dense: true,
          rows: '4',
          inputClasses: isEmailProtocol ? ['pt-5'] : [],
        },
      ];
    },
  },
  watch: {
    isMainInputDialogVisible() {
      if (this.selectedItem) this.payload = JSON.parse(JSON.stringify(this.selectedItem));
      else this.payload = { protocol: 'email' };
    },
  },
  methods: {
    ...call('sendMessage/*Configuration'),
    toggleDialog({ type = '', value = true, item = null } = {}) {
      if (item) {
        delete item?.hoverable;
        delete item?.listeners;
        this.selectedItem = item;
      }
      if (!value) this.selectedItem = null;
      this[`isMain${this.$_capitalize(type)}DialogLoaded`] = value;
      this[`isMain${this.$_capitalize(type)}DialogVisible`] = value;
    },
    dialogActionHandler(name) {
      const dict = {
        add: () => this.addEditConfigurationHandler('add'),
        edit: () => this.addEditConfigurationHandler('edit'),
        delete: () => this.deleteConfigurationHandler(),
      };
      dict[name]();
    },
    async addEditConfigurationHandler(fn) {
      this.isLoading = true;
      try {
        if (fn === 'edit') delete this.payload?.name;
        await this[`${fn}Configuration`](this.payload);
        this.toggleDialog({ type: 'input', value: false });
        this.payload = { protocol: 'email' };
      } catch (error) {
        console.log('  error', error);
      } finally {
        this.isLoading = false;
      }
    },
    async deleteConfigurationHandler() {
      this.isLoading = true;
      try {
        await this.deleteConfiguration(this.selectedItem.id);
        this.toggleDialog({ value: false });
        this.selectedItem = null;
      } catch (error) {
        console.log('  error', error);
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
